import { useState } from "react";
import api from "@/lib/api";
import { Info, X, Loader2, BarChart3, Users, MapPin, Globe } from "lucide-react";

function EmployerLogo({ name, domain }) {
  const [failed, setFailed] = useState(false);
  const src = domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=64` : null;
  if (!src || failed) {
    return (
      <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 text-xl font-bold shrink-0">
        {(name || "?")[0].toUpperCase()}
      </div>
    );
  }
  return (
    <img src={src} alt={name} onError={() => setFailed(true)}
      className="w-12 h-12 rounded-xl object-contain bg-white border border-slate-200 p-1 shrink-0" />
  );
}

export function EmployerInfoButton({ company, testid }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [employer, setEmployer] = useState(null);

  const openPanel = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const { data } = await api.get("/companies/employer", { params: { company } });
      setEmployer(data);
    } catch {
      setEmployer({ name: company });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid={testid}
        onClick={openPanel}
        title="About the employer"
        className="text-slate-400 hover:text-amber-600 transition-colors shrink-0"
      >
        <Info className="w-4 h-4" />
      </button>

      {/* Employer info slide-over */}
      <div
        data-testid={testid ? `${testid}-panel` : undefined}
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-[360px] bg-white shadow-2xl border-l border-slate-200 transform transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-900">About the Employer</h2>
          <button data-testid={testid ? `${testid}-close` : undefined} onClick={() => setOpen(false)}
            className="text-slate-400 hover:text-slate-700 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        {loading ? (
          <div className="py-20 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-amber-500" /></div>
        ) : employer ? (
          <div className="p-5 space-y-4">
            <div className="flex items-center gap-3">
              <EmployerLogo name={employer.name} domain={employer.domain} />
              <p className="font-bold text-slate-900 leading-snug" data-testid={testid ? `${testid}-name` : undefined}>{employer.name}</p>
            </div>
            <div className="rounded-xl border border-slate-100 divide-y divide-slate-100">
              {employer.industry && (
                <div className="flex items-center gap-3 px-4 py-3">
                  <BarChart3 className="w-4 h-4 text-slate-400 shrink-0" />
                  <div><p className="text-xs text-slate-400">Industry</p><p className="text-sm font-medium text-slate-900">{employer.industry}</p></div>
                </div>
              )}
              {employer.size && (
                <div className="flex items-center gap-3 px-4 py-3">
                  <Users className="w-4 h-4 text-slate-400 shrink-0" />
                  <div><p className="text-xs text-slate-400">Company Size</p><p className="text-sm font-medium text-slate-900">{employer.size}</p></div>
                </div>
              )}
              {employer.headquarters && (
                <div className="flex items-center gap-3 px-4 py-3">
                  <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
                  <div><p className="text-xs text-slate-400">Headquarters</p><p className="text-sm font-medium text-slate-900">{employer.headquarters}</p></div>
                </div>
              )}
              {employer.website && (
                <div className="flex items-center gap-3 px-4 py-3">
                  <Globe className="w-4 h-4 text-slate-400 shrink-0" />
                  <div>
                    <p className="text-xs text-slate-400">Website</p>
                    <a href={employer.website} target="_blank" rel="noreferrer"
                      className="text-sm font-medium text-amber-600 hover:underline break-all">
                      {employer.website.replace(/^https?:\/\//, "")}
                    </a>
                  </div>
                </div>
              )}
              {!employer.industry && !employer.size && !employer.headquarters && !employer.website && (
                <p className="px-4 py-6 text-sm text-slate-400 text-center">No additional employer details available yet.</p>
              )}
            </div>
          </div>
        ) : null}
      </div>
      {open && (
        <div className="fixed inset-0 z-40 bg-slate-900/30" onClick={() => setOpen(false)}
          data-testid={testid ? `${testid}-backdrop` : undefined} />
      )}
    </>
  );
}
